import { endpoints } from '@api'
import { Box, Button, Flex, Select, Text } from '@chakra-ui/react'
import { AssistIcon } from '@components/icons'
import { useAudioCapture, useExperienceManager, useWidget } from '@hooks'
import React, { useEffect, useState } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { useHistory, useParams } from 'react-router-dom'
import { useSessionControllerPostMarkCompleteV2 } from '~/clinician-api'

export const EndSession = ({ user }: { user: any }) => {
  const queryClient = useQueryClient()
  const history = useHistory()
  const { clientId } = useParams<{ clientId: string }>()
  const { sessionId } = useParams<{ sessionId: string }>()
  const { settings, selectClientOptions } = useWidget()
  const { isPlusPlanEnabled } = useExperienceManager()
  const { stopRecording, isUploadComplete } = useAudioCapture()
  const [selectedNoteType, setSelectedNoteType] = useState(
    selectClientOptions?.noteOptions?.noteType ??
      selectClientOptions?.noteOptions?.availableNoteTypes?.[0] ??
      user?.organization?.default_note_type
  )

  useEffect(() => {
    if (
      selectClientOptions?.noteOptions?.noteType &&
      selectedNoteType !== selectClientOptions?.noteOptions?.noteType
    )
      setSelectedNoteType(selectClientOptions?.noteOptions?.noteType)
  }, [selectClientOptions])

  const { mutateAsync: endSession } = useSessionControllerPostMarkCompleteV2()

  const {
    data: { progressNoteTypes }
  }: any = useQuery(
    [
      endpoints.getProgressNoteTypes.getCacheId(),
      user?.clinic?.organization_id
    ],
    () =>
      endpoints.getProgressNoteTypes.request({
        organizationId: user?.clinic?.organization_id
      }),
    {
      initialData: {
        progressNoteTypes: []
      },
      enabled: !!user
    }
  )

  const handleGenerateNote = async () => {
    await stopRecording()
    await endSession(
      {
        id: sessionId,
        data: {
          noteType: selectedNoteType,
          useDemoNotesWithEBC: false,
          shouldGenerateTxPlan: true,
          // shouldn't be getting passed from the frontend...
          assistEnabled: isPlusPlanEnabled,
          memoryEnabled: false,
          interventionSuggestionsEnabled: true,
          treatmentApproaches:
            selectClientOptions?.noteOptions?.treatmentApproaches ?? []
        }
      },
      {
        onSuccess() {
          // eslint-disable-next-line
          parent.postMessage(
            {
              type: 'BP_GENERATE_NOTE_CLICKED',
              sessionId,
              progressNoteId: sessionId,
              sessionType: selectClientOptions?.noteOptions?.sessionType,
              noteType: selectedNoteType,
              treatmentApproaches:
                selectClientOptions?.noteOptions?.treatmentApproaches
            },
            '*'
          )
          queryClient.invalidateQueries(
            endpoints.getPaywallValidation.getCacheId()
          )
        }
      }
    )
    history.push(
      `/mini-widget/patient/${clientId}/completed-session/${sessionId}`
    )
  }

  const filterProgressNoteTypes = selectClientOptions?.noteOptions?.noteGroup
    ? progressNoteTypes?.filter(
        (n: any) => n.noteGroup === selectClientOptions?.noteOptions?.noteGroup
      )
    : progressNoteTypes

  const availableProgressNoteTypes = selectClientOptions?.noteOptions
    ?.availableNoteTypes
    ? filterProgressNoteTypes.filter((t: any) =>
        selectClientOptions?.noteOptions?.availableNoteTypes?.includes(t.id)
      )
    : filterProgressNoteTypes

  return (
    <Flex flexDirection="column" justifyContent="space-between" height="100%">
      <Box>
        <Text mb="small">Note Type</Text>
        <Select
          mb="xsmall"
          borderColor="gray.300"
          borderRadius={
            settings.minifiedViewSettings?.inputs?.borderRadius ?? '5px'
          }
          _active={{ borderColor: 'gray.300' }}
          _focus={{ borderColor: 'gray.300' }}
          _hover={{ borderColor: 'gray.300' }}
          value={selectedNoteType}
          onChange={e => setSelectedNoteType(e.target.value)}
        >
          {availableProgressNoteTypes.map((n: any) => (
            <option key={n.id} value={n.id}>
              {n.display}
            </option>
          ))}
        </Select>
        <Text fontSize="10px">
          A note will be created from what was recorded in the session. It may
          take up to 2 minutes for the note to generate.
        </Text>
      </Box>
      <Button
        onClick={handleGenerateNote}
        backgroundColor={
          settings.minifiedViewSettings?.buttons?.primaryBackgroundColor ??
          'primary'
        }
        color={
          settings.minifiedViewSettings?.buttons?.primaryTextColor ?? 'white'
        }
        borderRadius={
          settings.minifiedViewSettings?.buttons?.borderRadius ?? '24px'
        }
        borderColor={
          settings.minifiedViewSettings?.buttons?.primaryBorderColor ??
          'primary'
        }
        borderWidth={
          settings.minifiedViewSettings?.buttons?.primaryBorderWidth ?? '1px'
        }
        isLoading={isUploadComplete}
      >
        <AssistIcon />
        <Text ml="xsmall">Generate Note</Text>
      </Button>
    </Flex>
  )
}
